import { useLocaleState, useTranslate } from "ra-core";

import type { Deal } from "@/components/atomic-crm/types";
import { cn } from "@/lib/utils";
import { usePipelineStages } from "./dealData";
import { daysInStage, isOpenStage, isOverdue, isPastLimit } from "./nextAction";

const useDayFormat = () => {
  const [locale] = useLocaleState();
  return (value: string) =>
    new Date(value).toLocaleDateString(locale, {
      timeZone: "Europe/Rome",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
};

const useStageOf = (deal: Pick<Deal, "stage_id">) => {
  const { data: stages = [] } = usePipelineStages();
  return stages.find((stage) => stage.id === deal.stage_id);
};

// On the deal cards (task 1.8): the next action of an open deal, in red
// when its date has passed.
export const TenaxNextActionBadge = ({ deal }: { deal: Deal }) => {
  const translate = useTranslate();
  const formatDay = useDayFormat();
  const stage = useStageOf(deal);
  if (!isOpenStage(stage) || !deal.next_action_at) return null;
  const overdue = isOverdue(deal.next_action_at);
  return (
    <span
      className={cn(
        "text-xs",
        overdue ? "font-medium text-destructive" : "text-muted-foreground",
      )}
    >
      {translate(
        overdue
          ? "tenax.deals.next_action.overdue"
          : "tenax.deals.next_action.on",
        { date: formatDay(deal.next_action_at) },
      )}
    </span>
  );
};

// On the deal page (task 1.8): next action, and days in the stage against
// the follow-up limit of the stage.
export const TenaxNextActionDetails = ({ deal }: { deal: Deal }) => {
  const translate = useTranslate();
  const formatDay = useDayFormat();
  const stage = useStageOf(deal);
  if (!isOpenStage(stage)) return null;
  const overdue = !!deal.next_action_at && isOverdue(deal.next_action_at);
  const days = deal.stage_entered_at
    ? daysInStage(deal.stage_entered_at)
    : null;
  const pastLimit =
    !!deal.stage_entered_at && isPastLimit(deal.stage_entered_at, stage);
  return (
    <>
      <div className="flex flex-col mr-10">
        <span className="text-xs text-muted-foreground tracking-wide">
          {translate("resources.deals.fields.next_action_at")}
        </span>
        <span
          className={cn("text-sm", overdue && "font-medium text-destructive")}
        >
          {deal.next_action_at ? formatDay(deal.next_action_at) : "—"}
        </span>
      </div>
      {days != null ? (
        <div className="flex flex-col mr-10">
          <span className="text-xs text-muted-foreground tracking-wide">
            {translate("tenax.deals.next_action.in_stage")}
          </span>
          <span
            className={cn(
              "text-sm",
              pastLimit && "font-medium text-destructive",
            )}
          >
            {stage?.max_days
              ? translate("tenax.deals.next_action.days_of_limit", {
                  smart_count: days,
                  max: stage.max_days,
                })
              : translate("tenax.deals.next_action.days", {
                  smart_count: days,
                })}
          </span>
        </div>
      ) : null}
    </>
  );
};
